import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, setCsrfToken, apiJson } from './client';
import { wipeAllPersistedCaches } from '../offline/persist';

export interface SessionUser {
  id: string;
  email: string;
  username: string;
  role: 'USER' | 'SUPERUSER' | 'ADMIN';
  mfaEnabled: boolean;
  emailVerified: boolean;
  impersonatorId: string | null;
}

export interface SessionResponse {
  user: SessionUser | null;
  impersonator?: { id: string; username: string; email: string } | null;
  csrfToken: string | null;
  expiresAt: number | null;
}

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const data = await api.get('auth/session').json<SessionResponse>();
      setCsrfToken(data.csrfToken);
      return data;
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { identifier: string; password: string; rememberMe?: boolean }) =>
      apiJson<SessionResponse | { mfaRequired: true; tmpToken: string }>('auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: async (data) => {
      if ('user' in data) {
        setCsrfToken(data.csrfToken);
        qc.setQueryData(['session'], data);
      }
    },
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { tmpToken: string; code: string; rememberMe?: boolean }) =>
      apiJson<SessionResponse>('auth/mfa/verify', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (data) => {
      setCsrfToken(data.csrfToken);
      qc.setQueryData(['session'], data);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; username: string; password: string; captchaToken?: string }) =>
      apiJson<SessionResponse>('auth/signup', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: (data) => {
      setCsrfToken(data.csrfToken);
      qc.setQueryData(['session'], data);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => apiJson<{ success: boolean }>('auth/logout', { method: 'POST', body: '{}' }),
    onSuccess: async () => {
      setCsrfToken(null);
      qc.setQueryData(['session'], { user: null, csrfToken: null, expiresAt: null });
      qc.clear();
      // Scrub persisted tenant data so a different user signing in later
      // never sees this user's cached groups/sets/links.
      await wipeAllPersistedCaches();
    },
  });
}
